import TourConfigBreadcrumbs from '@/features/dashboard/tourConfig/components/TourConfigBreadcrumbs.tsx';
import React from 'react';
import TourConfigHeader from '@/features/dashboard/tourConfig/components/TourConfigHeader.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { debugZodResolver } from '@/lib/debugZodResolver';
import { tourConfigInstallmentsRoute } from '@/features/dashboard/tourConfig/routes/tourConfigInstallmentsRoute.ts';
import {
  TourInstallmentForm,
  TourInstallmentInputForm,
  tourInstallmentsSchema,
} from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import TourInstallmentsConfigCard from '@/features/dashboard/tourConfig/components/TourInstallmentsConfigCard.tsx';
import useWriteTourInstallments from '@/features/dashboard/tourConfig/hooks/useWriteTourInstallments.ts';
import { getTourInstallmentsQueryOptions } from '@/features/dashboard/tourConfig/hooks/useGetTourInstallments.ts';
import { tourInstallmentDtoMapper } from '@/features/dashboard/tourConfig/types/TourInstallmentDto.mapper.ts';
import { useNavigate } from '@tanstack/react-router';
import BankAccountSelectSection from '@/features/dashboard/tourConfig/components/BankAccountSelectSection.tsx';
import CashInstructionCard from '@/features/dashboard/tourConfig/components/CashInstructionCard.tsx';
import { useSuspenseQuery } from '@tanstack/react-query';

const TourConfigInstallmentsPage = () => {
  const { tourId } = tourConfigInstallmentsRoute.useParams();
  const navigate = useNavigate();

  const { bankAccounts, priceRanges } =
    tourConfigInstallmentsRoute.useLoaderData();
  const { data: installments } = useSuspenseQuery(
    getTourInstallmentsQueryOptions(tourId),
  );

  const defaultValues: TourInstallmentInputForm =
    installments.installments.length > 0
      ? tourInstallmentDtoMapper.fromDtoToForm(
          installments,
          priceRanges,
          bankAccounts,
        )
      : {
          allowInstallments: false,
          installments: [{ paymentMethods: [] }],
          bankAccountSelections: [],
        };

  const tourInstallmentsForm = useForm<TourInstallmentInputForm>({
    resolver: debugZodResolver(tourInstallmentsSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { mutate, isPending } = useWriteTourInstallments(tourId);

  const onSubmit = async (values: TourInstallmentForm) => {
    mutate(values, {
      onSuccess: () =>
        navigate({
          to: `/app/tours/${tourId}/config/form`,
        }),
    });
  };

  return (
    <div className="space-y-4">
      <TourConfigBreadcrumbs />
      <FormProvider {...tourInstallmentsForm}>
        <form
          className="flex-1 space-y-4 pt-4"
          onSubmit={tourInstallmentsForm.handleSubmit(onSubmit)}
        >
          <TourConfigHeader
            title="Płatności w ratach"
            isLoading={isPending}
            submit={tourInstallmentsForm.handleSubmit(onSubmit)}
          />
          <div className="mx-auto max-w-[600px] space-y-2">
            <TourInstallmentsConfigCard />
            <BankAccountSelectSection />
            <CashInstructionCard />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default TourConfigInstallmentsPage;
